import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import type { CompanyClientResponse } from "../types/api.types";

interface Props {
  showModal: boolean;
  setShowModal: (open: boolean) => void;
  companyOptions: CompanyClientResponse[];
  onSubmitProp: (data: { company: string }) => void;
}

const normalize = (value: unknown): string =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("es-AR")
    .trim();

export default function LoginCompanyModal({
  showModal,
  setShowModal,
  companyOptions,
  onSubmitProp,
}: Props) {
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const theme = useTheme();

  useEffect(() => {
    if (showModal) {
      setSearch("");
      setQuery("");
      setPage(0);
      setSelectedId(null);
    }
  }, [showModal]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setQuery(search);
      setPage(0);
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [search]);

  // Prepare the searchable strings only when the source list changes.
  const indexed = useMemo(
    () =>
      companyOptions.map((company) => ({
        company,
        text: normalize(
          `${company.cuit ?? ""} ${company.comercialName ?? ""} ${company.legalName ?? ""}`,
        ),
        cuit: String(company.cuit ?? "").replace(/\D/g, ""),
      })),
    [companyOptions],
  );

  const filtered = useMemo(() => {
    const terms = normalize(query).split(/\s+/).filter(Boolean);
    return indexed
      .filter(({ text, cuit }) =>
        terms.every((term) => {
          const digits = term.replace(/\D/g, "");
          const isCuit = /^[\d.-]+$/.test(term) && digits.length > 0;
          return text.includes(term) || (isCuit && cuit.includes(digits));
        }),
      )
      .map(({ company }) => company);
  }, [indexed, query]);

  // Clamp the page if the source list changes while the modal is open.
  const currentPage = Math.min(
    page,
    Math.max(0, Math.ceil(filtered.length / rowsPerPage) - 1),
  );
  const visible = filtered.slice(
    currentPage * rowsPerPage,
    (currentPage + 1) * rowsPerPage,
  );
  const selected = companyOptions.find(
    (company) => company.clientID === selectedId,
  );
  const searchPending = search !== query;

  const confirm = () => {
    if (!selected) return;
    onSubmitProp({ company: selected.clientID });
    setShowModal(false);
  };

  return (
    <Dialog
      open={showModal}
      onClose={() => setShowModal(false)}
      fullWidth
      maxWidth="md"
      aria-labelledby="login-company-title"
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 8px 32px rgba(0, 119, 194, 0.12)",
        },
      }}
    >
      <DialogTitle sx={{ color: "primary.main", fontWeight: 600 }}>
        Seleccioná una empresa
      </DialogTitle>
      <DialogContent sx={{ width: "100%", boxSizing: "border-box" }}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          label="Buscar empresa"
          placeholder="CUIT, nombre comercial o razón social"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          sx={{ mt: 1, mb: 2 }}
        />
        <Typography
          variant="body2"
          color="text.secondary"
          aria-live="polite"
          sx={{ mb: 1 }}
        >
          {searchPending
            ? "Buscando…"
            : `${filtered.length} de ${companyOptions.length} empresas`}
        </Typography>
        <TableContainer
          sx={{
            borderRadius: 3,
            boxShadow: "0 4px 20px rgba(0, 119, 194, 0.1)",
            border: `1px solid ${theme.palette.primary.light}20`,
            overflow: "auto",
            maxWidth: "100%",
            width: "100%",
          }}
        >
          <Table
            size="small"
            stickyHeader
            aria-label="Empresas disponibles"
            sx={{ tableLayout: "fixed", minWidth: 740 }}
          >
            <colgroup>
              <col style={{ width: 48 }} />
              <col style={{ width: 145 }} />
              <col style={{ width: 240 }} />
              <col />
            </colgroup>
            <TableHead
              sx={{
                "& .MuiTableCell-head": {
                  backgroundColor: "primary.main",
                  color: "common.white",
                  fontWeight: 600,
                  borderBottom: "none",
                },
              }}
            >
              <TableRow>
                <TableCell padding="checkbox" aria-label="Selección" />
                <TableCell>CUIT</TableCell>
                <TableCell>Nombre comercial</TableCell>
                <TableCell>Razón social</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visible.map((company) => (
                <TableRow
                  key={company.clientID}
                  hover
                  selected={selectedId === company.clientID}
                  onClick={() => setSelectedId(company.clientID)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell padding="checkbox">
                    <Radio
                      size="small"
                      checked={selectedId === company.clientID}
                      onChange={() => setSelectedId(company.clientID)}
                      slotProps={{
                        input: {
                          "aria-label": `Seleccionar ${company.comercialName}, CUIT ${company.cuit}`,
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell
                    sx={{ fontFamily: "monospace", whiteSpace: "nowrap" }}
                  >
                    {company.cuit || "—"}
                  </TableCell>
                  <TableCell sx={{ overflowWrap: "anywhere" }}>
                    {company.comercialName || "—"}
                  </TableCell>
                  <TableCell sx={{ overflowWrap: "anywhere" }}>
                    {company.legalName || "—"}
                  </TableCell>
                </TableRow>
              ))}
              {visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    {companyOptions.length === 0
                      ? "No tenés empresas asignadas."
                      : "No se encontraron empresas para esa búsqueda."}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filtered.length}
          page={currentPage}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50]}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number(event.target.value));
            setPage(0);
          }}
          labelRowsPerPage="Por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} de ${count}`
          }
          getItemAriaLabel={(type) =>
            type === "next"
              ? "Página siguiente"
              : type === "previous"
                ? "Página anterior"
                : type === "first"
                  ? "Primera página"
                  : "Última página"
          }
          sx={{
            "& .MuiTablePagination-toolbar": {
              flexWrap: "wrap",
              justifyContent: "flex-end",
              pl: 0,
            },
            "& .MuiTablePagination-spacer": { display: "none" },
          }}
        />
        {selected && (
          <Typography variant="body2" sx={{ mt: 1, overflowWrap: "anywhere" }}>
            Seleccionada: {selected.cuit} - {selected.comercialName} -{" "}
            {selected.legalName}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setShowModal(false)}>Cancelar</Button>
        <Button variant="contained" disabled={!selected} onClick={confirm}>
          Continuar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
